import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { Player } from '@lordicon/react';
import {
  useTranslator,
  FormikInput as Input,
  useAppContext,
  formatCalories,
} from '@front-activerse/common-components';
import { Button } from 'reactstrap';
import MeasurementUnits from '../../Layout/MeasurementUnits';
import AmountWithUnitInput from './AmountWithUnitInput';
import TrackerFeedback from './TrackerFeedback';
import { TrackerMealAiModal } from './TrackerMeal/TrackerMealModals';
import { Ingredient } from '../types';
import {
  normalizeUnit,
  isSolidProduct,
  isLiquidProduct,
  getAvailableUnits,
  convertAmount,
  shortenUnit,
} from '../helpers/unitConversion';

export interface TrackerEditIngredientsFormProps {
  ingredients: Ingredient[];
  multiplier: number;
  values: { [key: string]: number | string | null };
  setFieldValue: (field: string, value: number | string) => void;
  onValuesChange: () => void;
  onDeleteIngredient: (id: string) => void;
  onAddIngredient: () => void;
  decimalSeparator: string;
  lastIngredientsCount: number;
  setLastIngredientsCount: (n: number) => void;
  /** For describe flow: show AI badge and TrackerFeedback */
  requestId?: string;
  showAiSection?: boolean;
}

/**
 * Form section for editing ingredient amounts/units, deleting, and adding ingredients.
 * Used inside TrackerAddEditMeal(wrapped in Formik).
 */
const TrackerEditIngredientsForm: React.FC<TrackerEditIngredientsFormProps> = ({
  ingredients,
  multiplier,
  values,
  setFieldValue,
  onValuesChange,
  onDeleteIngredient,
  onAddIngredient,
  decimalSeparator,
  lastIngredientsCount,
  setLastIngredientsCount,
  requestId,
  showAiSection,
}) => {
  const { t } = useTranslator();
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);

  console.debug('[TrackerEditIngredientsForm] props', {
    multiplier,
    ingredientsCount: ingredients.length,
    valuesKeys: Object.keys(values),
    perIngredient: ingredients.slice(0, 3).map((ing) => ({
      id: ing.id,
      name: ing.name,
      value: values[ing.id],
      valueType: typeof values[ing.id],
      unitValue: values[`${ing.id}_unit`],
    })),
  });
  const {
    consts: { APP_BRAND },
    images: { addIngredientIcon, aiBadgeTiny },
    lotties: { trashIcon, infoIcon },
  } = useAppContext();

  const playerRefs = useRef<Record<string, Player | null>>({});
  const playerRefInfo = useRef<Player>(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const handleUnitChange = useCallback(
    (
      ingredientId: string,
      newUnit: string,
      currentValues: { [key: string]: number | string | null }
    ) => {
      const currentAmount = currentValues[ingredientId];
      const currentUnit = (currentValues[`${ingredientId}_unit`] as string) || '';
      if (typeof currentAmount === 'number' && currentUnit && newUnit !== currentUnit) {
        const convertedAmount = convertAmount(currentAmount, currentUnit, newUnit);
        const roundedAmount = Math.round(convertedAmount * 100) / 100;
        setFieldValue(ingredientId, roundedAmount);
        setFieldValue(`${ingredientId}_unit`, newUnit);
      } else {
        setFieldValue(`${ingredientId}_unit`, newUnit);
      }
      onValuesChange();
    },
    [setFieldValue, onValuesChange]
  );

  const displayIngredients = useMemo(() => {
    return ingredients.map((ingredient) => {
      const formValue = values[ingredient.id];
      const selectedUnit = (values[`${ingredient.id}_unit`] as string) || null;
      if (formValue !== undefined && typeof formValue === 'number' && ingredient.measurements) {
        let amountPer1Portion = formValue / multiplier;
        const measurementIndex = ingredient.measurements.length > 1 ? 1 : 0;
        const currentMeasurement = ingredient.measurements[measurementIndex];
        const currentStoredAmount = currentMeasurement.amount;
        const originalUnit = normalizeUnit(currentMeasurement.unit);
        if (selectedUnit && selectedUnit !== originalUnit) {
          amountPer1Portion = convertAmount(amountPer1Portion, selectedUnit, originalUnit);
        }
        const ratio = currentStoredAmount > 0 ? amountPer1Portion / currentStoredAmount : 1;
        return {
          ...ingredient,
          measurements:
            ingredient.measurements.length > 1
              ? [
                  {
                    ...ingredient.measurements[0],
                    amount: Math.round(ingredient.measurements[0].amount * ratio * 100) / 100,
                  },
                  ingredient.measurements[1],
                ]
              : [ingredient.measurements[0]],
          kcal: Math.round(ingredient.kcal * ratio),
          macro: ingredient.macro
            ? {
                protein: {
                  grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                },
                fat: {
                  grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                },
                carbohydrates: {
                  grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                },
              }
            : undefined,
        };
      }
      return ingredient;
    });
  }, [ingredients, values, multiplier]);

  useEffect(() => {
    const timer = window.setTimeout(() => setLastIngredientsCount(displayIngredients.length), 1000);
    return () => window.clearTimeout(timer);
  }, [displayIngredients.length, setLastIngredientsCount]);

  return (
    <div className="tracker-ingredients-list">
      <div className="d-flex justify-content-between align-items-center mb-l">
        <p className="text-text-main-color semi-bold mb-0">
          {t('tracker.logMeal.ingredients.title')}
        </p>
        {showAiSection && (
          <div className="d-flex align-items-center">
            <img src={aiBadgeTiny} alt="" className="me-s" style={{ width: '39px' }} />
            <p className="mb-0 description text-text-variant-color">{t('tracker.aiSignature')}</p>
            <Button
              color="link"
              type="button"
              className="p-0 ms-s border-0 bg-transparent"
              onClick={() => setIsAiModalOpen(true)}
              aria-label={t('tracker.aiSignature.popup.title')}
              onMouseEnter={() => playerRefInfo.current?.playFromBeginning()}
              onTouchStart={() => playerRefInfo.current?.playFromBeginning()}
            >
              <Player
                ref={playerRefInfo}
                icon={infoIcon}
                size={20}
                colorize={iconsIconMiddleColor}
              />
            </Button>
          </div>
        )}
      </div>

      <ul className="tracker-ingredients-list-items">
        {displayIngredients.map((ingredient: Ingredient, index: number) => (
          <li
            key={ingredient.id}
            className={
              displayIngredients.length > lastIngredientsCount
                ? 'tracker-ingredient-item enable-animation'
                : 'tracker-ingredient-item'
            }
          >
            <div className="d-flex justify-content-between align-items-center">
              <div
                className="d-flex flex-column justify-content-start mb-m"
                style={{ maxWidth: '60%' }}
              >
                <span className="small text-text-main-color semi-bold text-truncate-2-line">
                  {ingredient.name}
                  {ingredient.brand && ` (${ingredient.brand})`}
                </span>
              </div>
              <span className="text-text-main-color semi-bold d-flex align-items-center">
                {(() => {
                  let currentMeasurement;
                  if (ingredient.measurements.length > 1) {
                    currentMeasurement = ingredient.measurements[1];
                  } else {
                    currentMeasurement = ingredient.measurements[0];
                  }
                  const originalUnit = normalizeUnit(currentMeasurement.unit);
                  const selectedUnit = (values[`${ingredient.id}_unit`] as string) || originalUnit;
                  const isSupportedUnit =
                    isSolidProduct(originalUnit) || isLiquidProduct(originalUnit);

                  if (!isSupportedUnit) {
                    return (
                      <>
                        <Input
                          name={ingredient.id}
                          type="number"
                          unit={shortenUnit(originalUnit)}
                          translateErrMsg={true}
                          step={0.1}
                          callbackOnChange={onValuesChange}
                        />
                        <Button
                          color="link"
                          type="button"
                          className="mb-m pe-0"
                          onClick={() => onDeleteIngredient(ingredient.id)}
                          onMouseEnter={() =>
                            playerRefs.current[ingredient.id]?.playFromBeginning()
                          }
                          onTouchStart={() =>
                            playerRefs.current[ingredient.id]?.playFromBeginning()
                          }
                        >
                          <Player
                            ref={(el) => {
                              playerRefs.current[ingredient.id] = el;
                            }}
                            icon={trashIcon}
                            size={20}
                            colorize={iconsIconMiddleColor}
                          />
                        </Button>
                      </>
                    );
                  }
                  const availableUnits = getAvailableUnits(
                    originalUnit,
                    (APP_BRAND as string) ?? ''
                  );
                  return (
                    <>
                      <AmountWithUnitInput
                        amountName={ingredient.id}
                        unitName={`${ingredient.id}_unit`}
                        availableUnits={availableUnits}
                        currentUnit={selectedUnit}
                        onUnitChange={(newUnit) => handleUnitChange(ingredient.id, newUnit, values)}
                        onAmountChange={onValuesChange}
                        step={0.1}
                        translateErrMsg={true}
                      />
                      <Button
                        color="link"
                        type="button"
                        className="pe-0"
                        onClick={() => onDeleteIngredient(ingredient.id)}
                        onMouseEnter={() => playerRefs.current[ingredient.id]?.playFromBeginning()}
                        onTouchStart={() => playerRefs.current[ingredient.id]?.playFromBeginning()}
                      >
                        <Player
                          ref={(el) => {
                            playerRefs.current[ingredient.id] = el;
                          }}
                          icon={trashIcon}
                          size={20}
                          colorize={iconsIconMiddleColor}
                        />
                      </Button>
                    </>
                  );
                })()}
              </span>
            </div>
            <div>
              <span className="description text-text-variant-2-color d-flex justify-content-between align-items-center">
                <span>
                  {formatCalories(ingredient.kcal * multiplier)} kcal
                  <span className="d-inline-block ms-3">
                    {t('summary.macros.proteinsShort')}{' '}
                    {(Math.round((ingredient.macro?.protein?.grams ?? 0) * multiplier * 10) / 10)
                      .toString()
                      .replace('.', decimalSeparator)}
                    g
                  </span>
                  <span className="d-inline-block ms-s">
                    {t('summary.macros.fatsShort')}{' '}
                    {(Math.round((ingredient.macro?.fat?.grams ?? 0) * multiplier * 10) / 10)
                      .toString()
                      .replace('.', decimalSeparator)}
                    g
                  </span>
                  <span className="d-inline-block ms-s">
                    {t('summary.macros.carbsShort')}{' '}
                    {(
                      Math.round((ingredient.macro?.carbohydrates?.grams ?? 0) * multiplier * 10) /
                      10
                    )
                      .toString()
                      .replace('.', decimalSeparator)}
                    g
                  </span>
                </span>
                <span style={{ paddingRight: '28px' }}>
                  {ingredient.measurements &&
                    ingredient.measurements[0].unit !== 'milliliters' &&
                    ingredient.measurements[0].unit !== 'cl' &&
                    ingredient.measurements[0].unit !== 'ml' &&
                    ingredient.measurements[0].unit !== 'oz' &&
                    ingredient.measurements[0].unit !== 'fl oz' &&
                    ingredient.measurements[0].unit !== 'fl_oz' &&
                    ingredient.measurements[0].unit !== 'g' &&
                    ingredient.measurements[0].unit !== 'grams' && (
                      <>
                        ≈
                        <MeasurementUnits
                          measurements={[ingredient.measurements[0]]}
                          multiplier={multiplier}
                          className="ms-1"
                        />
                      </>
                    )}
                </span>
              </span>
            </div>
          </li>
        ))}

        <div className="d-flex justify-content-center py-m">
          <Button color="link" onClick={onAddIngredient}>
            <img src={addIngredientIcon} className="me-1" alt="add" />
            <span className="semi-bold">
              {ingredients.length === 0
                ? t('tracker.logMeal.ingredients.addFirstIngredientButton')
                : t('tracker.logMeal.ingredients.addIngredientButton')}
            </span>
          </Button>
        </div>
      </ul>

      {showAiSection && requestId && <TrackerFeedback requestId={requestId} />}

      {showAiSection && (
        <TrackerMealAiModal
          isOpen={isAiModalOpen}
          onToggle={() => setIsAiModalOpen((prev) => !prev)}
          t={t}
        />
      )}
    </div>
  );
};

export default TrackerEditIngredientsForm;
